import { lazy } from 'react';
import { Link, Navigate, Route, Routes } from 'react-router-dom';

import { AuthProvider } from '@/auth/AuthProvider';
import { RequireAuth } from '@/auth/RequireAuth';
import { AppShell } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/sonner';
import { ConfirmScreen } from '@/features/auth/ConfirmScreen';
import { ForgotScreen } from '@/features/auth/ForgotScreen';
import { LoginScreen } from '@/features/auth/LoginScreen';
import { SignupScreen } from '@/features/auth/SignupScreen';
import { LibraryScreen } from '@/features/library/LibraryScreen';
import { SharePreviewScreen } from '@/features/share/SharePreviewScreen';
import { TonightScreen } from '@/features/tonight/TonightScreen';
import { useT } from '@/i18n/locale';
import { LocaleProvider } from '@/i18n/LocaleProvider';
import { AuthCallbackRoute } from '@/routes/auth-callback';
import { ThemeProvider } from '@/theme/ThemeProvider';

/*
 * The full route table from web/docs/screens.md §Route map.
 *
 * Public routes (login family + OAuth callback) render OUTSIDE the app shell;
 * everything else renders inside <AppShell> with page transitions, guarded by
 * <RequireAuth> (web/docs/data-layer.md §Route guards).
 *
 * The auth flow, Tonight (the home screen), the Library and the shell load
 * eagerly;
 * every other feature screen is code-split so the initial bundle stays small.
 * The <Suspense> boundary lives inside <AppShell> so the nav never flashes.
 */

const DiscoverScreen = lazy(() =>
  import('@/features/discover/DiscoverScreen').then((m) => ({ default: m.DiscoverScreen })),
);
const GenreScreen = lazy(() =>
  import('@/features/discover/GenreScreen').then((m) => ({ default: m.GenreScreen })),
);
const CalendarScreen = lazy(() =>
  import('@/features/calendar/CalendarScreen').then((m) => ({ default: m.CalendarScreen })),
);
const InboxScreen = lazy(() =>
  import('@/features/inbox/InboxScreen').then((m) => ({ default: m.InboxScreen })),
);
const ProfileScreen = lazy(() =>
  import('@/features/profile/ProfileScreen').then((m) => ({ default: m.ProfileScreen })),
);
const EditProfileScreen = lazy(() =>
  import('@/features/profile/EditProfile').then((m) => ({ default: m.EditProfileScreen })),
);
const SettingsScreen = lazy(() =>
  import('@/features/profile/SettingsScreen').then((m) => ({ default: m.SettingsScreen })),
);
const ShowDetailScreen = lazy(() =>
  import('@/features/show/ShowDetailScreen').then((m) => ({ default: m.ShowDetailScreen })),
);
const EpisodeDiscussionScreen = lazy(() =>
  import('@/features/discussion/EpisodeDiscussionScreen').then((m) => ({
    default: m.EpisodeDiscussionScreen,
  })),
);
const UserProfileScreen = lazy(() =>
  import('@/features/social/UserProfileScreen').then((m) => ({ default: m.UserProfileScreen })),
);
const ConnectionsScreen = lazy(() =>
  import('@/features/social/ConnectionsScreen').then((m) => ({ default: m.ConnectionsScreen })),
);
const PeopleSearchScreen = lazy(() =>
  import('@/features/social/PeopleSearchScreen').then((m) => ({ default: m.PeopleSearchScreen })),
);
const NotificationsScreen = lazy(() =>
  import('@/features/notif/NotificationsScreen').then((m) => ({ default: m.NotificationsScreen })),
);
const ListsScreen = lazy(() =>
  import('@/features/lists/ListsScreen').then((m) => ({ default: m.ListsScreen })),
);
const ListDetailScreen = lazy(() =>
  import('@/features/lists/ListDetailScreen').then((m) => ({ default: m.ListDetailScreen })),
);
const AddToListScreen = lazy(() =>
  import('@/features/lists/AddToListScreen').then((m) => ({ default: m.AddToListScreen })),
);

function NotFound() {
  const t = useT();
  return (
    <section className="flex min-h-[55dvh] flex-col items-start justify-center gap-4 py-16">
      <p className="text-kicker text-timecode text-ink-secondary">404</p>
      <h1 className="text-display-sm text-ink sm:text-display">{t('error404.title')}</h1>
      <p className="prose-measure text-body text-ink-secondary">{t('error404.body')}</p>
      <Button asChild variant="neutral">
        <Link to="/library">{t('common.backToLibrary')}</Link>
      </Button>
    </section>
  );
}

export function App() {
  return (
    <ThemeProvider>
      <LocaleProvider>
        <AuthProvider>
          <Routes>
            {/* public — outside the app shell */}
            <Route path="/login" element={<LoginScreen />} />
            <Route path="/signup" element={<SignupScreen />} />
            <Route path="/confirm" element={<ConfirmScreen />} />
            <Route path="/forgot" element={<ForgotScreen />} />
            <Route path="/auth/callback" element={<AuthCallbackRoute />} />
            {/* Public share preview (docs/sharing-plan.md §5.3) — every CTA
                works for a logged-out visitor, so it stays outside the shell
                and outside <RequireAuth>. */}
            <Route path="/s/:code" element={<SharePreviewScreen />} />

            {/* auth-gated — inside the app shell */}
            <Route element={<RequireAuth />}>
              <Route element={<AppShell />}>
                <Route path="/" element={<TonightScreen />} />
                <Route path="/library" element={<LibraryScreen />} />
                <Route path="/discover" element={<DiscoverScreen />} />
                <Route path="/discover/genre/:id" element={<GenreScreen />} />
                <Route path="/calendar" element={<CalendarScreen />} />
                <Route path="/discussions" element={<InboxScreen />} />
                {/* The Friends tab folded into Tonight — old links keep working. */}
                <Route path="/friends" element={<Navigate to="/" replace />} />
                <Route path="/profile" element={<ProfileScreen />} />
                <Route path="/profile/edit" element={<EditProfileScreen />} />
                <Route path="/settings" element={<SettingsScreen />} />
                <Route path="/show/:id" element={<ShowDetailScreen />} />
                <Route
                  path="/show/:id/episode/:season/:episode"
                  element={<EpisodeDiscussionScreen />}
                />
                <Route path="/user/:username" element={<UserProfileScreen />} />
                <Route path="/connections/:kind" element={<ConnectionsScreen />} />
                <Route path="/people-search" element={<PeopleSearchScreen />} />
                <Route path="/notifications" element={<NotificationsScreen />} />
                <Route path="/lists" element={<ListsScreen />} />
                <Route path="/lists/:id" element={<ListDetailScreen />} />
                <Route path="/lists/:id/add" element={<AddToListScreen />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Route>
          </Routes>
        </AuthProvider>
      </LocaleProvider>
      <Toaster />
    </ThemeProvider>
  );
}
